import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isAdminError, Registration, Webinar } from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";
import { bccEmailLink, emailBody, emailSubject, groupJoiningMessage, whatsAppNumber } from "../../webinars/messages";
import { copyText } from "../../webinars/clipboard";
import { formatLongDate } from "../../webinars/format";

interface ManualMessageDialogProps {
  webinar: Webinar;
  /** The attendees ticked in the list */
  registrations: Registration[];
  /** The webinar's saved joining link, or "" */
  meetingLink: string;
  onClose: () => void;
  notify: (message: string) => void;
}

// Joining instructions for the selected attendees, to send by hand: one message for everyone, the
// addresses for a BCC email, the numbers for WhatsApp, and a way to record that it was sent.
// Nothing is sent from here.
export default function ManualMessageDialog({ webinar, registrations, meetingLink, onClose, notify }: ManualMessageDialogProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [link, setLink] = useState(meetingLink);
  const [customMessage, setCustomMessage] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [result, setResult] = useState("");

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCloseRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const message = customMessage ?? groupJoiningMessage(webinar, link);
  const emails = registrations.map(r => r.email);
  const bcc = bccEmailLink(emails, emailSubject(webinar), emailBody(message));
  const numbers = registrations.map(r => ({ r, number: whatsAppNumber(r.phone) }));
  const readable = numbers.filter(n => n.number).map(n => `+${n.number}`);
  const unreadable = numbers.filter(n => !n.number).map(n => `${n.r.firstName} ${n.r.lastName}`);
  const notRecorded = registrations.filter(r => !r.instructionsSentAt);
  const count = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : word.endsWith("s") ? "es" : "s"}`;

  const copy = async (text: string, what: string) => {
    await copyText(text);
    notify(`${what} copied`);
  };

  const recordAll = async () => {
    if (!window.confirm(`Record joining instructions as sent by hand for ${count(notRecorded.length, "attendee")}? Do this after you've sent the message.`)) return;
    setRecording(true);
    setResult("");
    const sentAt = new Date().toISOString();
    let done = 0;
    try {
      for (const r of notRecorded) {
        await webinarRepository.updateRegistration(r.id, { instructionsSentAt: sentAt });
        done++;
      }
      setResult(`Recorded for ${count(done, "attendee")}. Their status now shows “✓ Sent by hand”.`);
      notify("Recorded as sent by hand");
    } catch (error) {
      setResult(`Recorded for ${count(done, "attendee")}, then stopped: ${isAdminError(error) ? error.message : "something went wrong. Try again for the rest."}`);
    } finally {
      setRecording(false);
    }
  };

  return createPortal(
    <div className="wa-modal-backdrop" onClick={onClose}>
      <div className="wa-modal wa-manual" role="dialog" aria-modal="true" aria-labelledby="wa-manual-title" onClick={e => e.stopPropagation()}>
        <div className="wa-modal-head">
          <div>
            <h2 id="wa-manual-title">Prepare a manual message</h2>
            <p className="wa-muted wa-manual-sub">
              {count(registrations.length, "selected attendee")} · {webinar.title} · {formatLongDate(webinar.startAt)}
            </p>
          </div>
          <button ref={closeRef} type="button" className="wa-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <label className="wa-field">
          Joining link <small>The webinar's saved link; change it here for this message only</small>
          <input type="url" placeholder="https://" value={link} onChange={e => setLink(e.target.value)} />
        </label>
        {!link.trim() && <p className="wa-warning">No joining link yet: the message will say “[Insert Webinar Link]”.</p>}

        <label className="wa-field">
          Message <small>One message for everyone, so it starts “Hello” rather than a name</small>
          <textarea rows={6} value={message} onChange={e => setCustomMessage(e.target.value)} />
        </label>
        {customMessage !== null && (
          <button type="button" className="wa-link" onClick={() => setCustomMessage(null)}>Reset to the standard message</button>
        )}

        <div className="wa-detail-section">
          <h3>Email</h3>
          <p className="wa-muted">One email with everyone in BCC, so nobody sees the others' addresses.</p>
          <div className="wa-row-actions">
            {bcc ? (
              <a className="wa-btn" href={bcc}>Prepare email (BCC)</a>
            ) : (
              <span className="wa-btn wa-btn--disabled" title="Too many addresses for an email app to open at once">Prepare email (BCC)</span>
            )}
            <button type="button" className="wa-btn" onClick={() => copy(emails.join(", "), "Email addresses")}>Copy {count(emails.length, "email address")}</button>
            <button type="button" className="wa-btn" onClick={() => copy(message, "Message")}>Copy message</button>
          </div>
          {!bcc && <p className="wa-muted">Too many addresses to open in an email app at once: copy them and paste them into BCC.</p>}
        </div>

        <div className="wa-detail-section">
          <h3>WhatsApp</h3>
          <p className="wa-muted">
            WhatsApp can't message many people from a link. Paste the numbers into a broadcast list (people only receive it
            if they've saved your number), or send from each person's record.
          </p>
          <div className="wa-row-actions">
            <button type="button" className="wa-btn" disabled={readable.length === 0} onClick={() => copy(readable.join("\n"), "WhatsApp numbers")}>
              Copy {count(readable.length, "WhatsApp number")}
            </button>
          </div>
          {unreadable.length > 0 && (
            <p className="wa-warning">
              {count(unreadable.length, "number")} couldn't be read for WhatsApp: {unreadable.slice(0, 5).join(", ")}{unreadable.length > 5 ? "…" : ""}
            </p>
          )}
        </div>

        <div className="wa-detail-section">
          <h3>After sending</h3>
          {notRecorded.length > 0 ? (
            <>
              <p className="wa-muted">Record it, so their status shows “✓ Sent by hand” and nobody is missed.</p>
              <button type="button" className="wa-btn" onClick={recordAll} disabled={recording}>
                {recording ? "Recording…" : `Record as sent by hand for ${count(notRecorded.length, "attendee")}`}
              </button>
            </>
          ) : (
            <p className="wa-muted">All of them are already recorded as sent by hand.</p>
          )}
          {result && <p className="wa-muted" role="status">{result}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
